import Immutable from 'immutable';
import { createStructuredSelector } from 'reselect';
import { StatusCodes } from 'http-status-codes';

import logger from 'logger';
import ApiService from 'services/ApiService/ApiService';
import NotificationService from 'services/NotificationService';
import ACCOUNT_TYPES from 'enum/accountTypes';
import { ENUM_PAYMENT_RESPONSE_DATA } from 'enum/payments';
import {
  convertTokensToDollars,
  getApiWarnMsg,
  isValidResponse,
  usernameOrImpersonate,
} from 'utils/helpers';
import makeAction from 'utils/makeAction';
import parseCatch from 'utils/parseCatch';

import {
  CANCEL_PENDING_REQUEST,
  EARNINGS_DETAILS_URL,
  EWALLET_ACCOUNT_URL,
  PAYOUT_HISTORY,
  PERFORMER_REVENUE_INSIGHTS,
} from './Api';


const REMOVE_PENDING_ENTRY = 'cam4/payout/REMOVE_PENDING_ENTRY';
const SET_EARNINGS_DETAILS = 'cam4/payout/SET_EARNINGS_DETAILS';
const SET_EARNINGS_DETAILS_BANNER = 'cam4/payout/SET_EARNINGS_DETAILS_BANNER';
const SET_HISTORY_REQUESTS = 'cam4/payout/SET_HISTORY_REQUESTS';
const SET_PENDING_REQUESTS = 'cam4/payout/SET_PENDING_REQUESTS';
const SET_PERFORMER_REVENUE = 'cam4/payout/SET_PERFORMER_REVENUE';
const SET_WALLET_AMOUNT_USD = 'cam4/payout/SET_WALLET_AMOUNT_USD';
const SET_WALLET_CURRENCY_AMOUNT = 'cam4/payout/SET_WALLET_CURRENCY_AMOUNT';
const SET_WALLET_DETAILS = 'cam4/payout/SET_WALLET_DETAILS';
const UPDATE_WALLET_BALANCE = 'cam4/payout/UPDATE_BALANCE';

const initialState = Immutable.fromJS({
  pendingRequests: [],
  historyRequests: [],
  walletDetails: {},
  earningsDetails: {},
  earningsDetailsBanner: {},
  performerRevenue: [],
});

export default function reducer(state = initialState, action) {
  switch (action.type) {
    case SET_HISTORY_REQUESTS:
      return state.set('historyRequests', Immutable.fromJS(action.requests));
    case SET_PENDING_REQUESTS:
      return state.set('pendingRequests', Immutable.fromJS(action.requests));
    case SET_EARNINGS_DETAILS:
      return state.set('earningsDetails', Immutable.fromJS(action.earningsDetails));
    case SET_EARNINGS_DETAILS_BANNER:
      return state.set('earningsDetailsBanner', Immutable.fromJS(action.earningsDetailsBanner));
    case SET_WALLET_DETAILS:
      return state.set('walletDetails', state.get('walletDetails').merge(Immutable.fromJS(action.walletDetails)));
    case SET_WALLET_CURRENCY_AMOUNT:
      return state.setIn(['walletDetails', 'currencyAmount'], Immutable.fromJS(action.amount));
    case SET_WALLET_AMOUNT_USD:
      return state.setIn(['walletDetails', 'amountUSD'], Immutable.fromJS(action.amount));
    case REMOVE_PENDING_ENTRY: {
      const filtered = state.get('pendingRequests').filter((item) => item.get('requestId') !== action.requestId);

      return state.set('pendingRequests', filtered);
    }
    case UPDATE_WALLET_BALANCE: {
      return state.setIn(['walletDetails', 'balance'], action.balance);
    }
    case SET_PERFORMER_REVENUE:
      return state.set('performerRevenue', Immutable.fromJS(action.performerRevenue));
    default:
      return state;
  }
}

export const NAME = 'payout';

const historyRequests = (state) => state[NAME].get('historyRequests');
const pendingRequests = (state) => state[NAME].get('pendingRequests');
const walletDetails = (state) => state[NAME].get('walletDetails');
const earningsDetails = (state) => state[NAME].get('earningsDetails');
const earningsDetailsBanner = (state) => state[NAME].get('earningsDetailsBanner');
const performerRevenue = (state) => state[NAME].get('performerRevenue');

const setPendingRequests = makeAction(SET_PENDING_REQUESTS, 'requests');
const setHistoryRequests = makeAction(SET_HISTORY_REQUESTS, 'requests');
const parseEarningsDetails = makeAction(SET_EARNINGS_DETAILS, 'earningsDetails');
const parseEarningsBannerDetails = makeAction(SET_EARNINGS_DETAILS_BANNER, 'earningsDetailsBanner');
const parseWalletDetails = makeAction(SET_WALLET_DETAILS, 'walletDetails');
const parseWalletCurrencyAmount = makeAction(SET_WALLET_CURRENCY_AMOUNT, 'amount');
const parseWalletAmountUSD = makeAction(SET_WALLET_AMOUNT_USD, 'amount');
const removePendingEntry = makeAction(REMOVE_PENDING_ENTRY, 'requestId');
const parseWalletBalance = makeAction(UPDATE_WALLET_BALANCE, 'balance');
const setPerformerRevenue = makeAction(SET_PERFORMER_REVENUE, 'performerRevenue');

function updateWalletBalance(balance) {
  return (dispatch) => {
    dispatch(parseWalletBalance(balance));
  };
}
function fetchPaymentRequests({ username, filter: { startDate, endDate }, isHistory }) {
  return async(dispatch) => {
    const url = encodeURI(PAYOUT_HISTORY(username));
    const from = startDate ? startDate.format('YYYY-MM-DD') : undefined;
    const to = endDate ? endDate.format('YYYY-MM-DD') : undefined;

    const params = { history: Boolean(isHistory) };

    if (isHistory) {
      params.from = from;
      params.to = to;
    }

    try {
      const res = await ApiService.get(url, { params });
      if (isValidResponse(res)) {
        const action = isHistory ? setHistoryRequests : setPendingRequests;
        const data = Array.isArray(res.data) ? res.data : [];
        dispatch(action(data));
      } else {
        logger.warn(getApiWarnMsg('fetchPaymentRequests', res.data));
      }
    } catch (e) {
      logger.error(e);
    }
  };
}

function cancelPendingRequest(requestId, headers, onRequestNotFoundMessage) {
  return (dispatch) => {
    const url = encodeURI(CANCEL_PENDING_REQUEST(requestId));

    ApiService.post(url, { headers, withCredentials: false, data: {} })
      .then((res) => {
        if (isValidResponse(res)) {
          if (res.data.errorCode === ENUM_PAYMENT_RESPONSE_DATA.REQUEST_NOT_FOUND) {
            NotificationService.create({
              type: NotificationService.type.TOAST,
              subtype: NotificationService.subtype.ERROR,
              text: onRequestNotFoundMessage,
            });
          } else {
            dispatch(removePendingEntry(requestId));
          }
        } else {
          logger.warn(getApiWarnMsg('cancelPendingRequest', res.data));
        }
      })
      .catch((e) => logger.error(e));
  };
}

function fetchEwalletAccountDetails(username, isFundsBanner = false) {
  return (dispatch) => {
    const url = encodeURI(EWALLET_ACCOUNT_URL(usernameOrImpersonate(username)));
    ApiService.get(url)
      .then((res) => {
        if (isValidResponse(res)) {
          const earningsData = res.data.find((accTypes) => accTypes.accountType === ACCOUNT_TYPES.EARNINGS) || {
            balance: 0,
            lifetimeBalance: 0,
            reservedBalance: 0,
            todayBalance: 0,
            ableToCashOut: false,
          };

          if (isFundsBanner) {
            dispatch(parseEarningsBannerDetails(earningsData));
          } else {
            dispatch(parseEarningsDetails(earningsData));
          }

          const walletData = res.data.find((accTypes) => accTypes.accountType === ACCOUNT_TYPES.WALLET);
          dispatch(parseWalletDetails(walletData || { balance: 0 }));
        } else {
          logger.warn(getApiWarnMsg('fetchEwalletAccountDetails', res.data));
        }
      })
      .catch((error) => {
        if (error.status === StatusCodes.NOT_FOUND) {
          logger.warn(`User "${username}" doesn't have payment account`);
        } else {
          parseCatch(error);
        }
      });
  };
}

function fetchCurrencyAmount(user) {
  return (dispatch) => {
    const url = encodeURI(EARNINGS_DETAILS_URL(usernameOrImpersonate(user.username)));
    ApiService.get(url)
      .then((res) => {
        if (isValidResponse(res)) {
          dispatch(parseWalletCurrencyAmount(res.data.currencyAmount));
          dispatch(parseWalletAmountUSD(res.data.amountUSD));
        } else {
          logger.warn(getApiWarnMsg('fetchCurrencyAmount', res.data));
        }
      })
      .catch((e) => logger.error(e));
  };
}

/**
 * parsing response and converting tokens to dollars
 * @param res
 * @returns {RequestInterface}
 */
function parsePerformerRevenueResults(res) {
  const data = [];
  if (Array.isArray(res?.data)) {
    res.data.forEach((val, index) => {
      data[index] = val;
      data[index].amount = convertTokensToDollars(val.amount);
      data[index].id = index;
      data[index].key = index;
      data[index].amountPerHour = convertTokensToDollars(
        data[index].hours ? (data[index].amount / data[index].hours).toFixed(2) : 0
      );
    });
  }

  return (dispatch) => {
    dispatch(setPerformerRevenue(data));
  };
}

function getPerformerRevenue(performerName, date) {
  const url = ApiService.substitute(PERFORMER_REVENUE_INSIGHTS, {
    performerName: encodeURIComponent(performerName),
    startDate: date[0],
    endDate: date[1],
  });

  return (dispatch) =>
    ApiService.get(url)
      .then((res) => {
        dispatch(parsePerformerRevenueResults(res));
      });
}

export const selector = createStructuredSelector({
  pendingRequests,
  historyRequests,
  walletDetails,
  earningsDetails,
  earningsDetailsBanner,
  performerRevenue,
});

export const actionCreators = {
  fetchEwalletAccountDetails,
  fetchPaymentRequests,
  cancelPendingRequest,
  updateWalletBalance,
  getPerformerRevenue,
  fetchCurrencyAmount,
};
